import CheckboxFormField from 'components/form/CheckboxFormField';
import SelectionFormField from 'components/form/SelectionFormField';
import TextFormField from 'components/form/TextFormField';
import { endOfYear } from 'date-fns';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Calendar,
  Grid,
} from 'grommet';
import AssetImageEditor from 'modules/admin/components/AssetImageEditor';
import MarkdownEditor from 'modules/admin/components/MarkdownEditor';
import { rawAsset$ } from 'modules/asset/asset$';
import { colors } from 'modules/root/theme';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dispatchAction } from 'rxbeach';
import { Asset, BaseAsset, BaseCourse, Course } from 'types';
import { array, boolean, number, object, string } from 'yup';
import { courseActions } from './course$';

const validationSchema = object({
  label: string().required('Navn på arrangement'),
  price: string().required('Pris for arrangement'),
  signupDeadline: string().required('Siste frist for påmelding'),
  location: string().required('Lokasjon'),
  maxSignups: number(),
  contact: string().required('Kontakt for spørsmål'),
  dates: array().of(string()),
  markdown: string().required('Skriv en beskrivelse av arrangmenetet'),
  featured: boolean(),
  published: boolean(),
});

const isNew = (test: any): test is BaseCourse[] => !test.id;

const initialValues: BaseCourse = {
  label: '',
  price: 0,
  signupDeadline: '',
  location: '',
  maxSignups: 0,
  contact: '',
  markdown: '',
  dates: [],
  shortid: '',
  type: '',
}

interface CourseEditorProps {
  asset: Asset | BaseAsset;
  onDelete: () => void;
}

export const CourseEditor = ({ asset, onDelete }: CourseEditorProps) => {
  const {
    touched,
    values,
    setValues,
    setFieldValue,
    errors,
    handleChange,
    handleBlur,
    submitForm,
  } = useFormik({
    initialValues: { ...initialValues, ...asset },
    validationSchema,
    onSubmit: (data) => {
      if (isNew(data)) dispatchAction(courseActions.apiCreate([data]));
      else dispatchAction(courseActions.apiPut([data as Course]));
    },
  });

  useEffect(() => {
    setValues({ ...initialValues, ...asset });
  }, [JSON.stringify(asset)]);

  const navigate = useNavigate();

  const formProps = { touched, errors, values, handleChange, handleBlur, setValues, setFieldValue };

  return (
    <>
      <Box direction="row" style={{ minHeight: '40px', height: "40px" }} margin="small">
        <Button
          size="small"
          primary
          label={isNew(values) ? 'Opprett' : 'Lagre'}
          disabled={Object.keys(errors).length !== 0 && Object.keys(touched).length !== 0}
          onClick={() => submitForm()}
        />
        {!isNew(values) && (
          <Button
            color={colors.danger.background}
            label="Slett"
            size="small"
            secondary
            onClick={() => {
              dispatchAction(courseActions.apiDelete([(asset as Asset).id]))
              onDelete()
            }}
          />
        )}
        {!isNew(values) && (
          <Button
            size="small"
            label="Til kurs siden"
            onClick={() => navigate(`/kurs/${asset.shortid}`)}
          />
        )}
      </Box>
      <Box overflow="auto">
        <Box direction="column" height="large" style={{ minHeight: 'unset' }} >
          <Grid columns={{ count: 2, size: 'auto' }} gap="small">
            <Box>
              <TextFormField {...formProps} label="Navn" id="label" />
              <CheckboxFormField {...formProps} label="Publisert" id="published" />
              <CheckboxFormField {...formProps} label="Fremhev" id="featured" />
              <TextFormField {...formProps} label="Lokasjon" id="location" />
              <TextFormField {...formProps} label="Kontakt" id="contact" />
              <TextFormField {...formProps} label="Siste påmeldings dato" id="signupDeadline" />
              <TextFormField {...formProps} label="Pris" id="price" />
              <TextFormField {...formProps} label="Maks antall" id="maxSignups" />
              <SelectionFormField {...formProps} label="Type tab" id="type" options={[...new Set(Object.values(rawAsset$.state.byId).filter(e => e.type === 'kurs').map(e => e.tab))]} />
            </Box>
            <Box>
              <Box align="center" justify="center" direction="column">
                <Calendar
                  daysOfWeek
                  dates={values.dates}
                  firstDayOfWeek={1}
                  size="medium"
                  onSelect={(date) => {
                    if (!date) return;
                    if (typeof date !== 'string') return;
                    if (values.dates.find((e) => e === date)) {
                      setValues({
                        ...values,
                        dates: values.dates.filter((e) => e !== date),
                      });
                    } else {
                      setValues({ ...values, dates: [...values.dates, date] });
                    }
                  }}
                // bounds={[new Date().toISOString(), endOfYear(new Date()).toISOString()]}
                />
                <AssetImageEditor {...formProps} />
              </Box>
            </Box>
          </Grid>
        </Box >
        <Box pad="medium" height="large" style={{ minHeight: 'unset' }}>
          <MarkdownEditor
            markdown={values.markdown}
            setMarkdown={(value: string) => setFieldValue('markdown', value)}
          />
        </Box>
      </Box>
    </>
  );
};

export default CourseEditor;
